import { MapTag } from './MapTag'
/**
 * MapExplorerView — the shared "map + Regions & Bonuses list" explorer.
 *
 * ONE implementation behind BOTH entry points so they never drift again:
 *   • app/(app)/maps/[id].tsx            — standalone map gallery explorer
 *   • app/(app)/games/[id]/map-details   — in-game map details (lobby / live)
 *
 * The in-game live "Game→Regions" view (ActiveGameView + RegionDetailsPanel)
 * is deliberately NOT routed through here — it has a different (action-panel)
 * layout and drives selection from game state; it shares only RegionAccordion +
 * MapView with this component. Keeping that path separate protects the live
 * game experience.
 *
 * Behaviour (identical in both entry points):
 *   • Tap a territory on the map → select it + expand its region in the list,
 *     which scrolls that region to the vertical centre (smooth).
 *   • Tap a region row → expand/collapse it on the map + list.
 *   • Tap the ocean / any non-territory area → reset to nothing selected and
 *     scroll the list to the top.
 *   • Map Reset (zoom-to-fit) → also collapse + scroll the list to the top.
 *   • Special-rules boards: the shared SpecialRulesCard (same as game setup)
 *     heads the list, above the regions.
 *   • Portrait: map + controls fixed on top, list scrolls independently below.
 *     Landscape: list in a left column, map fills the right.
 */

import React, { useMemo, useRef, useState, useEffect } from 'react'
import { View, Text, TouchableOpacity, ScrollView, useWindowDimensions } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { MapView } from './MapView'
import { MapControlsBar, MapControlsBarSlot, type MapViewMode } from './MapControlsBar'
import { RegionAccordion, NO_REGION_ID } from './RegionAccordion'
import { SpecialRulesCard } from './SpecialRulesCard'
import { hasSpecialRules } from '../../lib/gameSetupDraft'
import { useMapZoomControls } from '../../lib/useMapZoomControls'
import { useOrientation } from '../../hooks/useOrientation'
import { useTheme } from '../../hooks/useTheme'
import { lightenHex } from '../../lib/colorAdjust'
import { assignRegionColors, buildRegionAdjacency, BROAD_REGION_PALETTE } from './regionColors'
import type { HydratedGameState as GameState } from '../../lib/gameState'
import type { MapDef } from '@warcarp/api-types'

export interface MapExplorerViewProps {
  /** The map to explore (already fetched by the screen). */
  map: MapDef
  /** Back-button handler (screens differ in how they pop the stack). */
  onBack: () => void
  /** Region to focus on mount (deep-link), or null. */
  initialRegionId?: string | null
  /**
   * Show the Game|Regions mode toggle. In-game map-details wants it (peek the
   * board vs region bonuses); the standalone gallery explorer doesn't (there's
   * no game to view), so it's always in regions mode. Default true.
   */
  showModeToggle?: boolean
}

export function MapExplorerView({
  map,
  onBack,
  initialRegionId = null,
  showModeToggle = true,
}: MapExplorerViewProps) {
  const { width: screenWidth } = useWindowDimensions()
  const { isLandscape } = useOrientation()

  // Region colours: graph-coloured against region adjacency + honouring any
  // MapDef.Region.color pins — the SAME mapping the in-game view uses, so the
  // explorer never disagrees with what players see in a live game.
  const regionColors = useMemo(
    () => assignRegionColors(map.regions, buildRegionAdjacency(map.territories, map.regions), BROAD_REGION_PALETTE),
    [map],
  )

  // Region outlines + bonus badges. Tap a territory to inspect it; tap a region
  // row to focus it. The two selections are mutually exclusive.
  const [selectedTerritoryId, setSelectedTerritoryId] = useState<string | null>(null)
  const [selectedRegionId, setSelectedRegionId] = useState<string | null>(initialRegionId)

  // The accordion's scroll container (one mounts per orientation). Tapping a
  // territory scrolls its region to centre; Reset / ocean-tap scroll to top.
  const scrollRef = useRef<ScrollView>(null)
  const scrollViewH = useRef(0)

  const zoom = useMapZoomControls(map.viewBox)
  const [mapMode, setMapMode] = useState<MapViewMode>('regions')

  const selected = useMemo(
    () => map.territories.find((t) => t.id === selectedTerritoryId) ?? null,
    [map, selectedTerritoryId],
  )
  const adjacentIds = useMemo(() => selected?.adjacencies ?? [], [selected])

  // Synthetic state wrapper for MapView (it expects a full GameState).

  // The map's own backdrop (MapDef.backdropColor) fills the map area top to
  // bottom, as on the game screen; the controls bar and list keep the paper.
  const { theme } = useTheme()
  const backdrop = map.backdropColor?.[theme === 'dark' ? 'dark' : 'light']
  const backdropStyle = backdrop ? { backgroundColor: backdrop } : undefined
  const fakeState = useMemo<GameState>(
    () => ({
      map,
      players: [],
      turnIndex: 0,
      phase: 'claim',
      eliminated: [],
      config: {
        startingTroops: 20,
        maxAttacksPerTurn: null,
        maxFortifiesPerTurn: 1,
        maxTurns: 500,
        autoAssignTerritories: false,
        eliminatePlayerBonus: 5,
        cardBonusSchedule: 'cycle',
      },
      attacksThisTurn: 0,
      fortifiesThisTurn: 0,
      armiesToPlace: {},
      roundsPlayed: 0,
      mapId: map.id,
    }),
    [map],
  )

  function handleTerritoryPress(tid: string) {
    // Map tap: select the territory AND expand its region so the highlighted
    // territory is visible in the list under its region tile.
    // Region-less territories land in the accordion's synthetic "Unaligned"
    // group, so expand that instead of leaving the tap with nowhere to go.
    const regionId = map.territories.find((t) => t.id === tid)?.regionId ?? NO_REGION_ID
    setSelectedTerritoryId((cur) => (cur === tid ? null : tid))
    setSelectedRegionId(regionId)
  }

  function handleRegionToggle(rid: string) {
    setSelectedRegionId((cur) => (cur === rid ? null : rid))
    setSelectedTerritoryId(null)
  }

  function handleTerritorySelect(tid: string) {
    setSelectedTerritoryId((cur) => (cur === tid ? null : tid))
  }

  function resetList() {
    // Close (collapse / clear selection) then scroll the list to the top —
    // deferred a frame so the collapse has laid out.
    setSelectedRegionId(null)
    setSelectedTerritoryId(null)
    requestAnimationFrame(() => scrollRef.current?.scrollTo({ y: 0, animated: true }))
  }

  const controlsBar = (
    <MapControlsBar
      onReset={() => {
        zoom.reset()
        resetList()
      }}
      onZoomIn={zoom.zoomIn}
      onZoomOut={zoom.zoomOut}
      canZoomIn={zoom.canZoomIn}
      canZoomOut={zoom.canZoomOut}
      // Omitting mode/onModeChange hides the Game|Regions toggle (MapControlsBar).
      {...(showModeToggle ? { mode: mapMode, onModeChange: setMapMode } : {})}
    />
  )

  const infoPanel = (
    <View className="px-3 py-3">
      {hasSpecialRules(map) && (
        <View style={{ marginBottom: 12 }}>
          <SpecialRulesCard map={map} padding={12} testID="map-special-rules" />
        </View>
      )}
      <RegionAccordion
        regions={map.regions}
        territories={map.territories}
        regionColors={regionColors}
        expandedRegionId={selectedRegionId}
        selectedTerritoryId={selectedTerritoryId}
        onToggleRegion={handleRegionToggle}
        onSelectTerritory={handleTerritorySelect}
        scrollParentRef={scrollRef}
        getViewportHeight={() => scrollViewH.current}
      />
    </View>
  )

  // Region-related MapView props — identical across both orientations. The glow
  // is drawn from the raw region colour inside MapView; the lightened tint here
  // is only the selected region's territory FILL.
  const mapRegionProps = {
    allTerritoriesInteractive: true as const,
    hideBadges: true as const,
    highlightRegions: regionColors,
    selectedTerritoryId: selectedTerritoryId ?? undefined,
    onTerritoryPress: handleTerritoryPress,
    onBackgroundPress: resetList,
    selectedRegionId: selectedRegionId ?? undefined,
    // The synthetic "Unaligned" group has no colour, so there is nothing to
    // lighten — passing undefined leaves those territories untinted.
    selectedRegionColor: selectedRegionId && regionColors[selectedRegionId]
      ? lightenHex(regionColors[selectedRegionId]!, 0.55)
      : undefined,
    // No toggle → always show region bonuses (regions-only explorer).
    showRegionBonuses: showModeToggle ? mapMode === 'regions' : true,
    regionFocusId: selectedRegionId ?? undefined,
    adjacentTerritoryIds: adjacentIds,
    hideOnMapZoomButtons: true as const,
    zoomEnabled: true as const,
    zoomScale: zoom.scale,
    panOffset: zoom.pan,
    onPanChange: zoom.setPan,
    onZoomScaleChange: zoom.setScale,
  }

  const backTile = (
    <TouchableOpacity onPress={onBack} accessibilityLabel="Go back" accessibilityRole="button" className="p-1">
      <Text className="font-typed text-blood text-sm">← Back</Text>
    </TouchableOpacity>
  )

  return (
    <SafeAreaView className="flex-1 bg-canvas">
      {!isLandscape && (
        <View className="flex-row items-center px-4 py-2 border-b border-olive gap-2">
          {backTile}
          <Text className="font-stencil text-ink text-base uppercase flex-1" numberOfLines={1}>
            {map.name}
          </Text>
          {map.legacy === 'warfish' && <MapTag variant="warfish" />}
        </View>
      )}

      {isLandscape ? (
        // ── Landscape: left column (back/title + list) + map fills the right ──
        <View className="flex-1 flex-row">
          <View
            className="flex-col border-r border-olive"
            style={{ flexBasis: '30%', flexShrink: 0, flexGrow: 0, minWidth: 350 }}
          >
            <View className="flex-row items-center gap-2 px-3 py-2 border-b border-olive">
              {backTile}
              <Text className="font-stencil text-ink text-base uppercase flex-1" numberOfLines={1}>
                {map.name}
              </Text>
              {map.legacy === 'warfish' && <MapTag variant="warfish" />}
            </View>
            <ScrollView
              ref={scrollRef}
              onLayout={(e) => {
                scrollViewH.current = e.nativeEvent.layout.height
              }}
              className="flex-1"
              contentContainerStyle={{ paddingTop: 12, paddingBottom: 12 }}
              showsVerticalScrollIndicator={false}
            >
              {infoPanel}
            </ScrollView>
          </View>
          <View className="flex-1">
            <View className="flex-1 items-center justify-center overflow-hidden" style={backdropStyle}>
              <MapView
                state={fakeState}
               
                viewerSlot={1}
                reservedLeft={Math.max(350, Math.round(screenWidth * 0.3))}
                reservedBottom={44}
                {...mapRegionProps}
              />
            </View>
            <MapControlsBarSlot state={fakeState}>{controlsBar}</MapControlsBarSlot>
          </View>
        </View>
      ) : (
        // ── Portrait: map + controls fixed on top; list scrolls below ────────
        <View className="flex-1">
          <View className="items-center" style={backdropStyle}>
            <MapView state={fakeState} viewerSlot={1} reservedBottom={44} {...mapRegionProps} />
          </View>
          {controlsBar}
          <ScrollView
            ref={scrollRef}
            onLayout={(e) => {
              scrollViewH.current = e.nativeEvent.layout.height
            }}
            className="flex-1"
            contentContainerStyle={{ paddingBottom: 12 }}
            showsVerticalScrollIndicator={false}
          >
            {infoPanel}
          </ScrollView>
        </View>
      )}
    </SafeAreaView>
  )
}
